import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

export function TestimonialsCarousel() {
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/testimonials`);
      const data = await response.json();
      setTestimonials(data.filter(t => t.featured));
    } catch (err) {
      console.error('Failed to load testimonials:', err);
    } finally {
      setLoading(false);
    }
  };

  const goToNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goToPrevious = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Auto-advance carousel every 8 seconds
  useEffect(() => {
    if (testimonials.length === 0) return;
    
    const timer = setInterval(() => {
      goToNext();
    }, 8000);

    return () => clearInterval(timer);
  }, [testimonials.length, currentIndex]);

  if (loading) {
    return (
      <div className="py-16 text-center">
        <p className="text-[hsl(var(--bk-fg-muted))]">Loading testimonials...</p>
      </div>
    );
  }

  if (testimonials.length === 0) {
    return null;
  }

  const currentTestimonial = testimonials[currentIndex];

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  return (
    <section className="py-5 sm:py-7 bg-[hsl(var(--bk-bg-elev))]" data-testid="testimonials-carousel">
      <div className="max-w-[900px] mx-auto px-5 sm:px-8">
        <div className="text-center mb-8">
          <p className="font-mono-meta text-[hsl(var(--bk-accent))] mb-3">Client Testimonials</p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-[hsl(var(--bk-fg))]">
            What our clients say
          </h2>
        </div>

        <div className="relative">
          {/* Carousel Container */}
          <div className="relative overflow-hidden">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
                className="py-4"
              >
                <div className="text-center">
                  {/* Quote Icon */}
                  <Quote size={40} className="text-[hsl(var(--bk-accent))]/20 mx-auto mb-4" />

                  {/* Quote Text */}
                  <blockquote className="font-body text-lg sm:text-xl text-[hsl(var(--bk-fg))] italic mb-6 leading-relaxed">
                    "{currentTestimonial.quote}"
                  </blockquote>

                  {/* Attribution */}
                  <div className="space-y-1">
                    <p className="font-display text-base text-[hsl(var(--bk-fg))]">
                      {currentTestimonial.client_name}
                    </p>
                    {currentTestimonial.client_title && (
                      <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                        {currentTestimonial.client_title}
                        {currentTestimonial.client_company && `, ${currentTestimonial.client_company}`}
                      </p>
                    )}
                    {currentTestimonial.project_slug && (
                      <Link 
                        to={`/work/${currentTestimonial.project_slug}`}
                        className="inline-flex items-center gap-2 font-body text-sm text-[hsl(var(--bk-accent))] hover:opacity-80 transition-opacity mt-3"
                      >
                        View Project →
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Arrows */}
          {testimonials.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-12 p-3 rounded-full bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] hover:bg-[hsl(var(--bk-accent))] hover:text-white hover:border-[hsl(var(--bk-accent))] transition-all"
                aria-label="Previous testimonial"
                data-testid="testimonial-prev-btn"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-12 p-3 rounded-full bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] hover:bg-[hsl(var(--bk-accent))] hover:text-white hover:border-[hsl(var(--bk-accent))] transition-all"
                aria-label="Next testimonial"
                data-testid="testimonial-next-btn"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}

          {/* Dots Indicator */}
          {testimonials.length > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setDirection(index > currentIndex ? 1 : -1);
                    setCurrentIndex(index);
                  }}
                  className={`h-2 rounded-full transition-all ${
                    index === currentIndex
                      ? 'w-8 bg-[hsl(var(--bk-accent))]'
                      : 'w-2 bg-[hsl(var(--bk-line))] hover:bg-[hsl(var(--bk-line-2))]'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
